import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { imageFallback, resolveImageUrl } from '../lib/imageUtils';

const ProductCard = ({ product }) => {
  const { addToCart, toggleWishlist, isInWishlist } = useCart();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleAddToCart = (e) => {
    e.preventDefault();
    addToCart(product);
  };

  const handleToggleWishlist = (e) => {
    e.preventDefault();
    toggleWishlist(product);
  };

  const imageUrl = resolveImageUrl(product.images?.[0], BACKEND_URL);

  const hasDiscount = product.offer_applied || product.final_price < product.price;

  return (
    <Link to={`/products/${product.id}`} className="group block" data-testid={`product-card-${product.id}`}>
      <div className="product-card relative overflow-hidden bg-[#141414] border border-transparent hover:border-white/10">
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            src={imageUrl}
            alt={product.name}
            onError={imageFallback}
            className="w-full h-full object-cover"
          />
          
          {product.stock === 0 && (
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
              <span className="text-white uppercase tracking-wider text-sm">Out of Stock</span>
            </div>
          )}

          {hasDiscount && product.stock > 0 && (
            <div className="absolute top-4 right-4 bg-[#D4AF37] text-black px-3 py-1 text-xs font-medium uppercase tracking-wider">
              {product.discount_type === 'percentage' ? `${product.discount_value}% OFF` : `₹${product.discount_value} OFF`}
            </div>
          )}

          <div className="absolute top-4 left-4">
            <button
              onClick={handleToggleWishlist}
              className="w-10 h-10 bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-colors"
              data-testid={`wishlist-button-${product.id}`}
            >
              <Heart
                size={18}
                className={isInWishlist(product.id) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-white'}
              />
            </button>
          </div>

          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <button
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              className="px-8 py-3 bg-[#D4AF37] text-black font-medium tracking-wide uppercase text-sm hover:bg-[#F1E5AC] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              data-testid={`add-to-cart-button-${product.id}`}
            >
              <ShoppingCart size={16} />
              <span>Add to Cart</span>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-3">
          <div className="space-y-1">
            <p className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
              {product.subcategory_name || product.category_name || 'Jewelry'}
            </p>
            <h3 className="text-xl font-light text-white tracking-tight" data-testid={`product-name-${product.id}`}>
              {product.name}
            </h3>
          </div>

          <div className="flex items-baseline space-x-3">
            <span className="text-2xl font-light text-[#D4AF37]" data-testid={`product-price-${product.id}`}>
              ₹{product.final_price.toLocaleString()}
            </span>
            {hasDiscount && (
              <span className="text-sm text-[#A3A3A3] line-through">
                ₹{product.price.toLocaleString()}
              </span>
            )}
          </div>

          {product.stock > 0 && product.stock < 5 && (
            <p className="text-xs text-[#FF3B30] uppercase tracking-wider">
              Only {product.stock} left
            </p>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
